const DEFAULT_FLUSH_MS = 250;

/**
 * Batches ids arriving in a burst into a single handler call, and never runs the handler for an id
 * that is already in flight. A document emitting ten GENERATING -> READY progress events becomes
 * one fetch.
 *
 * @param {Function} handler - async function(ids: string[]) => void
 * @param {{flushMs?: number}} [options]
 * @returns {{push: Function, flush: Function, cancel: Function}}
 */
export const createCoalescer = (handler, { flushMs = DEFAULT_FLUSH_MS } = {}) => {
  const pending = new Set();
  const inFlight = new Set();
  let timer = null;
  let cancelled = false;

  const run = async () => {
    timer = null;
    if (cancelled) {
      return;
    }

    const batch = [];
    for (const id of pending) {
      if (!inFlight.has(id)) {
        batch.push(id);
        inFlight.add(id);
      }
    }
    pending.clear();

    if (batch.length === 0) {
      return;
    }

    try {
      await handler(batch);
    } catch (error) {
      console.error('[coalescer] Handler failed:', error);
    } finally {
      batch.forEach((id) => inFlight.delete(id));
      // An id that changed again while we were fetching needs another pass.
      if (pending.size > 0 && !timer && !cancelled) {
        timer = setTimeout(run, flushMs);
      }
    }
  };

  return {
    push(id) {
      if (cancelled || id == null) {
        return;
      }
      pending.add(id);
      if (!timer) {
        timer = setTimeout(run, flushMs);
      }
    },

    flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      return run();
    },

    cancel() {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      pending.clear();
      inFlight.clear();
    }
  };
};
